import { ActorDatabaseContext } from "@-/dev.improve.haniverse.contexts.database.actor";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { useActorTableChangeVersion } from "@-/dev.improve.haniverse.lib.database.change.actor";
import { createUseQueryOptions } from "@-/dev.improve.haniverse.lib.query-options";

// Every card carrying a "skip until tomorrow" deferral, past or present. The read is deliberately
// NOT filtered by `now`: a query config is built at render, and a fetch-time cutoff would either
// freeze the clock in the key or force a raw read. Consumers compare `deferredUntil` against their
// own `now` (see `isGraphemeDeferralActive`), which also lets them tell an ACTIVE deferral (hold
// the card back) from an EXPIRED one (the learner is owed this card again).
const DEFERRED_GRAPHEMES_CONFIG = {
  columns: { deferredUntil: true },
  where: { deferredUntil: { isNotNull: true } },
  with: { userGraphemeMeaningCard: { columns: { grapheme: true } } },
} as const;

export function useGetDeferredGraphemesQueryKey() {
  const [actorDb] = useContextSelectors(ActorDatabaseContext, (context) => context.db);
  const withTableChangeVersion = useActorTableChangeVersion("userCard", DEFERRED_GRAPHEMES_CONFIG);
  return () => withTableChangeVersion(["deferredGraphemes", { actorDb }] as const);
}

/**
 * `grapheme → deferredUntil` (ISO) for every card that has ever been deferred and not since
 * enrolled. Enrollment clears `deferredUntil`, so an entry here is either a live deferral or an
 * expired one the lesson resolver should surface again.
 */
export const [useDeferredGraphemesQueryOptions, deferredGraphemesQueryFn] = createUseQueryOptions(
  useGetDeferredGraphemesQueryKey,
  async ({ queryKey: [_, { actorDb }] }) => {
    // Soft-delete filter is injected by the wrapped `actorDb` query proxy.
    const deferredCards = await actorDb(import.meta.location).query.userCard.findMany(
      DEFERRED_GRAPHEMES_CONFIG,
    );

    const deferredUntilByGrapheme = new Map<string, string>();
    for (const card of deferredCards) {
      const grapheme = card.userGraphemeMeaningCard?.grapheme;
      if (grapheme !== undefined && card.deferredUntil !== null) {
        deferredUntilByGrapheme.set(grapheme, card.deferredUntil);
      }
    }
    return deferredUntilByGrapheme;
  },
);
